import { useEffect } from 'react';
import useUserSettings from '@/lib/queries/user/useUserSettings';
import { useStytchSession } from '@stytch/react';

declare global {
  interface Window {
    IzWidget: any;
  }
}

interface UseInformizelyOptions {
  enabled?: boolean;
  pageId?: string;
}

/**
 * Hook to inject Informizely feedback widget for pilot program users
 * @param options Configuration options for the widget
 */
function useInformizely(options: UseInformizelyOptions = {}) {
  const { session, fromCache } = useStytchSession();
  const { enabled = true, pageId } = options;
  const { useGetUserSettings: useGetUser } = useUserSettings();
  const { data: userSettings } = useGetUser({
    enabled: !!session && !fromCache,
  });

  const isPilotUser = userSettings?.pilot ?? false;
  const shouldLoadWidget = enabled && isPilotUser;

  useEffect(() => {
    if (!shouldLoadWidget) {
      return;
    }

    if (window.IzWidget) {
      console.log('Informizely widget already loaded globally');
      return;
    }

    const loadInformizely = () => {
      window.IzWidget = window.IzWidget || {};

      const scriptElement = document.createElement('script');
      scriptElement.type = 'text/javascript';
      scriptElement.async = true;
      scriptElement.src = import.meta.env.VITE_INFORMIZELY_SRC || null;

      if (!scriptElement.src) {
        throw new Error('VITE_INFORMIZELY_SRC is not defined');
      }

      // Add error handling
      scriptElement.onerror = () => {
        console.error('Failed to load Informizely widget');
      };

      scriptElement.onload = () => {
        console.log('Informizely widget loaded successfully', pageId ? `for page: ${pageId}` : '');
      };

      document.head.appendChild(scriptElement);
    };

    const timeoutId = setTimeout(loadInformizely, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [shouldLoadWidget, pageId]);

  return {
    isLoaded: !!window.IzWidget,
    isPilotUser,
    shouldLoadWidget,
  };
}

export default useInformizely;
